import type { JSX } from 'solid-js';
import { t } from '../i18n';
import type { EnrollRefusal } from '../lib/enroll-refusal';
import { BoxRequirements } from './BoxRequirements';
import { EnrollTransportToken } from './EnrollTransportToken';
import { SshEnrollPanel } from './SshEnrollPanel';
import { Tabs, type TabsItem } from './Tabs';

// The two ways to connect a machine, as one tabbed body — the Connect a machine
// dialog's enroll body (#6567), with the donate wizard still its second
// consumer (arc 101 slice 11; two consumers justify the abstraction, one did
// not).
//
//   token — mint a one-time `dev_ren_` token, reveal it ONCE, and the operator
//           runs the connect command on the box themselves. The command carries
//           the typed coding model as an export when it is valid, and asks for
//           it at a prompt otherwise — never the token.
//   ssh   — hand us host/port/user and the platform dials in and runs the join;
//           the token never leaves the server, so there is nothing to reveal.
//
// Both panels stay MOUNTED, toggled with `hidden` (`<Tabs/>`'s contract):
// unmounting the token panel to peek at the other tab would drop a revealed
// one-time token whose seat slot is already burnt for an hour. Whoever owns the
// surrounding overlay disposes both, which is where the plaintext dies — so
// `open` is threaded through rather than read from a parent's lifecycle.

export type ConnectMachineBodyProps = {
  /** The surrounding overlay's open state — a close re-arms both panels. */
  readonly open: boolean;
  readonly token: string | null;
  /** Called after a successful mint / queued bootstrap so the parent can refetch. */
  readonly onEnrolled: () => void;
  /** The panels' own dismiss action (the dialog's Cancel / Done buttons). */
  readonly onClose: () => void;
  /**
   * Namespace for the tab/panel element ids. Two consumers mean two id sets, and
   * duplicate ids would point a screen reader's `aria-controls` at the wrong panel.
   */
  readonly idPrefix?: string;
  /** A dialog shell with a plan line takes the plan refusals (`EnrollTransportToken`'s prop). */
  readonly onRefusal?: ((refusal: EnrollRefusal | null) => void) | undefined;
};

export function ConnectMachineBody(props: ConnectMachineBodyProps): JSX.Element {
  // Built once: a fresh array per read would hand `<For>` new rows and remount
  // the panels — the one thing the hidden-not-swapped rule exists to prevent.
  const items: readonly TabsItem[] = [
    {
      id: 'token',
      label: t('fleet.connect.transport_token'),
      panel: () => (
        <EnrollTransportToken
          open={props.open}
          token={props.token}
          onMinted={props.onEnrolled}
          onClose={props.onClose}
          onRefusal={props.onRefusal}
        />
      ),
    },
    {
      id: 'ssh',
      label: t('fleet.connect.transport_ssh'),
      panel: () => (
        <SshEnrollPanel
          open={props.open}
          token={props.token}
          onQueued={props.onEnrolled}
          onClose={props.onClose}
          onRefusal={props.onRefusal}
        />
      ),
    },
  ];
  // What the box must be is said ONCE above both transports: either way the same
  // join runs on it (plan 102 slice 05).
  return (
    <>
      <BoxRequirements />
      <Tabs
        label={t('fleet.connect.transport_label')}
        idPrefix={props.idPrefix ?? 'connect-machine'}
        items={items}
      />
    </>
  );
}
